import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { saveAs } from 'file-saver';
import { notificationInit } from '../../modules/notifications/actions';
import { REQUEST_ERROR_MESSAGE } from './constants';
import { getContactsRequest, updateContactDefaultRequest, getBlobForReqListRequest } from '../../api/requests';
import {
	GET_CONTACTS,
	UPDATE_CONTACT_DEFAULT,
	GET_LIST_CONTACT_FILE_TYPES,
	setContactsAction,
	setIsContactsLoadingAction,
	setPaginationAction,
	setIsDownloadLoadingAction,
} from './actions';
import { DISMISS_TIME } from '../../api/constants';
import { getContactsDefaultSelector } from './selectors';
import { errorHandler } from '../../api/utils';
import { removeModalQuery } from '../../utils/removeModalQuery';

export function* getContactsSaga({ payload: { searchQuery, id } }) {
	try {
		yield put(setIsContactsLoadingAction(true));

		const query = new URLSearchParams(searchQuery);
		query.delete('filters');
		query.delete('sorts');

		const str = removeModalQuery(
			query.toString().replace('childrenSorts', 'sorts').replace('childrenFilters', 'filters'),
		);

		const { data, message, meta, toast } = yield call(
			getContactsRequest,
			`${str ? `${str}&receive=all` : '?receive=all'}`,
			id,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const query = new URLSearchParams(searchQuery);
			query.set('modal', 'contact');

			yield put(
				setContactsAction(
					data?.map(contact => ({
						...contact,
						name: contact.name || '-',
						email: contact.email || '-',
						link: `?${query.toString() ? `${query.toString()}&` : ''}contactId=${contact.id}`,
					})),
				),
			);

			if (meta) {
				yield put(
					setPaginationAction({
						current_page: meta.current_page,
						per_page: meta.per_page,
						from: meta.from,
						last_page: meta.last_page,
						total: meta.total,
					}),
				);
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsContactsLoadingAction(false));
	}
}

export function* updateContactDefaultSaga({ payload: { addressId, contactId, value, searchQuery } }) {
	try {
		const { data, message, toast } = yield call(
			updateContactDefaultRequest,
			{ is_default: value },
			addressId,
			contactId,
		);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: `${toast?.link}${searchQuery ? `&${searchQuery}` : ''}` } : {}),
					}),
				);
			}

			const contacts = yield select(getContactsDefaultSelector());

			yield put(
				setContactsAction(
					contacts.map(contact => {
						return contact.id === data.id
							? { ...contact, is_default: data.is_default }
							: { ...contact, is_default: data.is_default ? false : contact.is_default };
					}),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	}
}

export function* getReqListFileSaga({ payload: { exportUrl } }) {
	try {
		yield put(setIsDownloadLoadingAction(true));
		const blob = yield call(getBlobForReqListRequest, exportUrl);

		saveAs(blob, 'contacts.xlsx');
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsDownloadLoadingAction(false));
	}
}

export default function* contactsSaga() {
	yield takeEvery(GET_CONTACTS, getContactsSaga);
	yield takeEvery(UPDATE_CONTACT_DEFAULT, updateContactDefaultSaga);
	yield takeEvery(GET_LIST_CONTACT_FILE_TYPES, getReqListFileSaga);
}
